import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ButtonShortcut } from "@/components/sidebar/Button";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import { canDuplicateAtCursor } from "../input/utils/duplicateNodeAtCursor";
import { SuggestionsProvider } from "../suggestions/SuggestionsProvider";

import { ComputedRefIndex, LABEL_CORNERS } from "./ComputedRefIndex";
import { CorneredBox } from "./CorneredBox";
import { InlineNode } from "./InlineNode";
import { useComputedDoc } from "./utils/useComputedDoc";

import type { SuggestionProviderType } from "../suggestions/types";
import type { ExpressionComponent } from "@/components/expression/types";
import type { ComputedNodeManager } from "@/models/computedNode/ComputedNodeManager";
import type { Hotkey } from "@/utils/hotkeys";
import type { ComputedState } from "@corca/data";

const ACCEPTED_PROVIDERS: SuggestionProviderType[] = ["computedCommand", "clipboardCommand"];

export const ComputedResultNode: ExpressionComponent<"computedResult"> = observer(
  function ComputedResultNode({ node }) {
    const doc = useComputedDoc(node);
    const { computedNodes } = doc;
    const t = useTranslations();

    let hotkey: Hotkey | null = null;

    const { state, invalidReason } = node.props;
    const refLabel = computedNodes.getComputedRefLabel(node);
    let tooltipTitle: string | undefined;
    if (state === "error") {
      tooltipTitle = t("computedNode.tooltip.error");
    } else if (state === "invalid") {
      if (invalidReason === "cycle") tooltipTitle = t("computedNode.tooltip.cycle");
      else if (invalidReason === "missing-ref") tooltipTitle = t("computedNode.tooltip.missingRef");
      else if (invalidReason === "not-computable")
        tooltipTitle = t("computedNode.tooltip.notComputable");
      else tooltipTitle = t("computedNode.tooltip.invalid");
    } else if (canDuplicateAtCursor(doc, node)) {
      tooltipTitle = t("computedNode.tooltip.referenceAtCursor");
      hotkey = "reference-at-cursor";
    }

    const { handlerProps, tooltip } = useTooltip({
      title: hotkey ? <ButtonShortcut text={tooltipTitle} hotkey={hotkey} /> : tooltipTitle,
      placement: "right",
      hoverProps: {
        delay: {
          open: 1000,
        },
      },
    });
    const classNames = getComputedClassNames(computedNodes, state);

    return (
      <SuggestionsProvider accept={ACCEPTED_PROVIDERS}>
        <div
          {...handlerProps}
          data-testid="computed-result"
          data-state={state ?? "pending"}
          data-invalid-reason={invalidReason}
          className={twMerge("flex mx-1")}
        >
          <div className="relative">
            <CorneredBox
              className={twMerge(
                "-inset-x-[4px] -inset-y-[2px]",
                computedNodes.isActiveResult(node.id) ? classNames.active : classNames.default,
              )}
              exclude={refLabel ? LABEL_CORNERS : undefined}
            />
            {node.inlineNode && <InlineNode node={node.inlineNode} className="" />}
          </div>
          {refLabel && <ComputedRefIndex label={refLabel} />}
        </div>
        {tooltip}
      </SuggestionsProvider>
    );
  },
);

function getComputedClassNames(
  computedNodes: ComputedNodeManager,
  state: ComputedState | undefined,
) {
  if (!computedNodes.ready) {
    return {
      default: "text-gray-500/50",
      active: "text-gray-500/50",
    };
  }
  if (state !== "valid") {
    return {
      default: "text-red-500",
      active: "text-red-500 bg-red-50 dark:bg-red-950",
    };
  }
  return {
    default: "text-blue-500",
    active: "text-blue-500 bg-blue-50 dark:bg-blue-950",
  };
}
